import { base64ToBytes, bytesToBase64 } from "./credential-encryption";
import { CREDENTIAL_TRANSFER_ALGORITHM, type AgentCredentialPublicKey, type EncryptedCredential } from "./types";

export interface CredentialGrantKey {
  publicKey: AgentCredentialPublicKey;
  decryptCredential: (credential: EncryptedCredential) => Promise<string>;
}

function getSubtle(): SubtleCrypto {
  const subtle = globalThis.crypto?.subtle;
  if (!subtle) throw new Error("WebCrypto indisponible pour déchiffrer le grant.");
  return subtle;
}

function copyToArrayBuffer(bytes: Uint8Array<ArrayBufferLike>): ArrayBuffer {
  const copy = new Uint8Array(bytes.byteLength);
  copy.set(bytes);
  return copy.buffer;
}

function base64Url(data: ArrayBuffer): string {
  return bytesToBase64(data).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/g, "");
}

function validateCredential(credential: EncryptedCredential): EncryptedCredential {
  if (
    credential?.alg !== CREDENTIAL_TRANSFER_ALGORITHM ||
    !credential.encryptedKey ||
    !credential.iv ||
    !credential.ciphertext
  ) {
    throw new Error("Grant credential invalide.");
  }
  return credential;
}

export async function createCredentialGrantKey(): Promise<CredentialGrantKey> {
  const subtle = getSubtle();
  const keyPair = (await subtle.generateKey(
    {
      name: "RSA-OAEP",
      modulusLength: 2048,
      publicExponent: new Uint8Array([1, 0, 1]),
      hash: "SHA-256",
    },
    true,
    ["encrypt", "decrypt"],
  )) as CryptoKeyPair;
  const spki = await subtle.exportKey("spki", keyPair.publicKey);
  const kid = `operator:${base64Url(await subtle.digest("SHA-256", spki)).slice(0, 32)}`;

  const publicKey: AgentCredentialPublicKey = {
    type: "agent:credential_key",
    v: 1,
    alg: CREDENTIAL_TRANSFER_ALGORITHM,
    kid,
    spki: bytesToBase64(spki),
    issuedAt: new Date().toISOString(),
    encoding: "base64",
  };

  return {
    publicKey,
    decryptCredential: async (input) => {
      const credential = validateCredential(input);
      const wrappedKey = base64ToBytes(credential.encryptedKey);
      const iv = base64ToBytes(credential.iv);
      const ciphertext = base64ToBytes(credential.ciphertext);
      const rawAesKey = await subtle.decrypt({ name: "RSA-OAEP" }, keyPair.privateKey, copyToArrayBuffer(wrappedKey));
      try {
        const aesKey = await subtle.importKey("raw", rawAesKey, { name: "AES-GCM" }, false, ["decrypt"]);
        const plaintext = await subtle.decrypt(
          { name: "AES-GCM", iv: copyToArrayBuffer(iv) },
          aesKey,
          copyToArrayBuffer(ciphertext),
        );
        const plaintextBytes = new Uint8Array(plaintext);
        try {
          return new TextDecoder().decode(plaintextBytes);
        } finally {
          plaintextBytes.fill(0);
        }
      } finally {
        new Uint8Array(rawAesKey).fill(0);
      }
    },
  };
}
